import React, { useState, useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import { Card, Row, Col, Badge, Button, Dropdown, ButtonGroup, Tab, Nav, Table, Spinner, Modal, Alert, OverlayTrigger, Tooltip as BSTooltip } from "react-bootstrap";
import PerfectScrollbar from "react-perfect-scrollbar";
import ReactApexChart from "react-apexcharts";
import { useTranslation } from "react-i18next";
import ProviderMap from './LeafletProviderMap';
import { getProvidersStatusAction } from '../../../store/actions/RapportAction';

const ProviderDashboard = ({ providers, loading, error, stats, dateRange, refreshTrigger, viewMode }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    // États locaux pour les filtres
    const [filteredProviders, setFilteredProviders] = useState([]);
    const [selectedProvider, setSelectedProvider] = useState(null);
    const [statusFilter, setStatusFilter] = useState('all');
    const [cityFilter, setCityFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [locationClusters, setLocationClusters] = useState([]);
    const [mapView, setMapView] = useState('standard'); // 'standard', 'heat', 'clusters'

    // Obtenir les villes et types uniques pour les filtres
    const cities = Array.isArray(providers) && providers.length > 0
        ? Array.from(new Set(providers.map(p => p.city))).map(city => {
            const cityItem = providers.find(p => p.city === city);
            return { id: cityItem.cityId, name: city };
        })
        : [];

    const types = Array.isArray(providers) && providers.length > 0
        ? Array.from(new Set(providers.map(p => p.providerTypeName))).map(type => {
            const typeItem = providers.find(p => p.providerTypeName === type);
            return { id: typeItem.providerTypeId, name: type };
        })
        : [];

    // Charger les données au chargement du composant et à chaque changement de dates
    useEffect(() => {
        loadProvidersData();
    }, [dateRange, refreshTrigger]);

    // Fonction pour charger les données des prestataires
    const loadProvidersData = () => {
        dispatch(getProvidersStatusAction({
            dateDebut: dateRange.startDate,
            dateFin: dateRange.endDate
        }));
    };

    // Appliquer les filtres sur les données
    useEffect(() => {
        if (!Array.isArray(providers) || providers.length === 0) return;

        let result = [...providers];

        // Filtre par statut
        if (statusFilter !== 'all') {
            result = result.filter(p => statusFilter === 'online' ? p.isOnline : !p.isOnline);
        }

        // Filtre par ville
        if (cityFilter !== 'all') {
            result = result.filter(p => p.cityId === parseInt(cityFilter));
        }

        // Filtre par type de prestataire
        if (typeFilter !== 'all') {
            result = result.filter(p => p.providerTypeId === parseInt(typeFilter));
        }

        // Recherche par terme
        if (searchTerm.trim() !== '') {
            const term = searchTerm.toLowerCase();
            result = result.filter(p =>
                p.providerName.toLowerCase().includes(term) ||
                p.city.toLowerCase().includes(term) ||
                p.providerTypeName.toLowerCase().includes(term) ||
                (p.email && p.email.toLowerCase().includes(term))
            );
        }

        // Analyser les clusters de localisation
        const clusters = analyzeLocations(result);

        setLocationClusters(clusters);
        setFilteredProviders(result);
    }, [statusFilter, cityFilter, typeFilter, searchTerm, providers]);

    // Fonction pour analyser les emplacements et créer des clusters
    const analyzeLocations = (providerList) => {
        // Logique simplifiée pour créer des clusters basés sur les coordonnées
        const locations = {};

        providerList.forEach(provider => {
            if (provider.lat && provider.lon) {
                // Arrondir les coordonnées pour regrouper les points proches
                const key = `${Math.round(provider.lat * 100) / 100},${Math.round(provider.lon * 100) / 100}`;

                if (!locations[key]) {
                    locations[key] = {
                        lat: provider.lat,
                        lon: provider.lon,
                        count: 0,
                        online: 0,
                        offline: 0,
                        providers: []
                    };
                }

                locations[key].count++;
                locations[key].providers.push(provider);

                if (provider.isOnline) {
                    locations[key].online++;
                } else {
                    locations[key].offline++;
                }
            }
        });

        return Object.values(locations);
    };

    // Calculer le temps écoulé depuis la dernière activité
    const getTimeAgo = (dateString) => {
        if (!dateString) return "Inconnu";

        const now = new Date();
        const time = new Date(dateString);
        const diffMs = now - time;
        const diffMins = Math.floor(diffMs / 60000);

        if (diffMins < 1) return "À l'instant";
        if (diffMins < 60) return `Il y a ${diffMins} min`;

        const diffHours = Math.floor(diffMins / 60);
        if (diffHours < 24) return `Il y a ${diffHours}h`;

        const diffDays = Math.floor(diffHours / 24);
        return `Il y a ${diffDays}j`;
    };

    // Options pour le graphique circulaire
    const statusChartOptions = {
        chart: {
            type: 'donut',
            fontFamily: 'inherit',
            animations: {
                enabled: true,
                easing: 'easeinout',
                speed: 800
            },
            background: 'transparent'
        },
        colors: ['#2bc155', '#f94687'],
        labels: ['En ligne', 'Hors ligne'],
        legend: {
            position: 'bottom',
            horizontalAlign: 'center',
            fontSize: '14px',
            markers: {
                width: 10,
                height: 10,
                radius: 5
            },
            itemMargin: {
                horizontal: 10,
                vertical: 5
            }
        },
        plotOptions: {
            pie: {
                donut: {
                    size: '65%',
                    background: 'transparent',
                    labels: {
                        show: true,
                        name: {
                            show: true,
                            fontSize: '16px',
                            offsetY: -10
                        },
                        value: {
                            show: true,
                            fontSize: '24px',
                            fontWeight: 'bold',
                            formatter: function (val) {
                                return val;
                            }
                        },
                        total: {
                            show: true,
                            label: 'Total',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            formatter: function (w) {
                                return w.globals.seriesTotals.reduce((a, b) => a + b, 0);
                            }
                        }
                    }
                }
            }
        },
        dataLabels: {
            enabled: false
        },
        responsive: [{
            breakpoint: 480,
            options: {
                chart: {
                    height: 250
                },
                legend: {
                    position: 'bottom'
                }
            }
        }],
        tooltip: {
            enabled: true,
            y: {
                formatter: function (val) {
                    return val + ' prestataires';
                }
            }
        }
    };

    // Options pour le graphique de répartition par type
    const typeDistributionOptions = {
        chart: {
            type: 'bar',
            height: 350,
            fontFamily: 'inherit',
            toolbar: {
                show: false
            },
            animations: {
                enabled: true,
                easing: 'easeinout',
                speed: 800
            },
            background: 'transparent'
        },
        plotOptions: {
            bar: {
                horizontal: true,
                barHeight: '70%',
                distributed: false,
                borderRadius: 5,
                dataLabels: {
                    position: 'top'
                }
            }
        },
        colors: ['#2bc155', '#f94687'],
        dataLabels: {
            enabled: true,
            textAnchor: 'start',
            style: {
                fontSize: '12px',
                fontWeight: 'bold',
                colors: ['#fff']
            },
            formatter: function (val, opt) {
                return val;
            },
            offsetX: 0
        },
        stroke: {
            width: 0
        },
        xaxis: {
            categories: types.map(t => t.name),
            labels: {
                style: {
                    fontSize: '12px'
                }
            }
        },
        yaxis: {
            labels: {
                show: true,
                style: {
                    fontSize: '12px'
                }
            }
        },
        legend: {
            position: 'bottom',
            horizontalAlign: 'center',
            fontSize: '14px',
            markers: {
                radius: 5
            }
        },
        tooltip: {
            shared: false,
            y: {
                formatter: function (val) {
                    return val + ' prestataires';
                }
            }
        }
    };

    // Préparer les séries pour le graphique par type
    const prepareTypeData = () => {
        if (!Array.isArray(providers) || providers.length === 0) return [];

        const onlineByType = {};
        const offlineByType = {};

        types.forEach(type => {
            onlineByType[type.name] = 0;
            offlineByType[type.name] = 0;
        });

        providers.forEach(provider => {
            if (provider.isOnline) {
                onlineByType[provider.providerTypeName] = (onlineByType[provider.providerTypeName] || 0) + 1;
            } else {
                offlineByType[provider.providerTypeName] = (offlineByType[provider.providerTypeName] || 0) + 1;
            }
        });

        return [
            {
                name: 'En ligne',
                data: types.map(type => onlineByType[type.name] || 0)
            },
            {
                name: 'Hors ligne',
                data: types.map(type => offlineByType[type.name] || 0)
            }
        ];
    };

    // Gestionnaires pour le Modal
    const handleOpenModal = (provider) => {
        setSelectedProvider(provider);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        // Réinitialiser selectedProvider après la fermeture du modal
        setTimeout(() => setSelectedProvider(null), 300);
    };

    // Actualiser manuellement les données
    const handleRefresh = () => {
        loadProvidersData();
    };

    // Changer le type de vue de la carte
    const handleMapViewChange = (view) => {
        setMapView(view);
    };

    return (
        <div className={`provider-dashboard ${viewMode === 'compact' ? 'compact-view' : ''}`}>
            {/* Filtres avancés */}
            <div className="filter-container mb-4">
                <Card className="shadow-sm border-0">
                    <Card.Body className="p-3">
                        <div className="d-flex flex-wrap align-items-center">
                            <div className="search-box flex-grow-1 mr-3 mb-2 mb-md-0">
                                <div className="input-group">
                                    <div className="input-group-prepend">
                                        <span className="input-group-text bg-transparent border-right-0">
                                            <i className="fa fa-search text-muted"></i>
                                        </span>
                                    </div>
                                    <input
                                        type="text"
                                        className="form-control border-left-0"
                                        placeholder="Rechercher un prestataire..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                    {searchTerm && (
                                        <div className="input-group-append">
                                            <button
                                                className="btn btn-outline-secondary border-left-0"
                                                type="button"
                                                onClick={() => setSearchTerm('')}
                                            >
                                                <i className="fa fa-times"></i>
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="filter-buttons d-flex flex-wrap">
                                <Dropdown className="mr-2 mb-2 mb-md-0">
                                    <Dropdown.Toggle variant="outline-primary" size="sm" id="status-filter" className="px-3">
                                        <i className="fa fa-wifi mr-1"></i> Statut: {statusFilter === 'all' ? 'Tous' : statusFilter === 'online' ? 'En ligne' : 'Hors ligne'}
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                        <Dropdown.Item active={statusFilter === 'all'} onClick={() => setStatusFilter('all')}>
                                            <i className="fa fa-globe mr-2"></i> Tous
                                        </Dropdown.Item>
                                        <Dropdown.Item active={statusFilter === 'online'} onClick={() => setStatusFilter('online')}>
                                            <i className="fa fa-check-circle text-success mr-2"></i> En ligne
                                        </Dropdown.Item>
                                        <Dropdown.Item active={statusFilter === 'offline'} onClick={() => setStatusFilter('offline')}>
                                            <i className="fa fa-times-circle text-danger mr-2"></i> Hors ligne
                                        </Dropdown.Item>
                                    </Dropdown.Menu>
                                </Dropdown>

                                <Dropdown className="mr-2 mb-2 mb-md-0">
                                    <Dropdown.Toggle variant="outline-primary" size="sm" id="city-filter" className="px-3">
                                        <i className="fa fa-map-marker-alt mr-1"></i> Ville: {cityFilter === 'all' ? 'Toutes' : cities.find(c => c.id.toString() === cityFilter)?.name || 'Toutes'}
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu className="dropdown-menu-custom" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                        <Dropdown.Item active={cityFilter === 'all'} onClick={() => setCityFilter('all')}>
                                            <i className="fa fa-globe mr-2"></i> Toutes les villes
                                        </Dropdown.Item>
                                        <Dropdown.Divider />
                                        {cities.map(city => (
                                            <Dropdown.Item
                                                key={city.id}
                                                active={cityFilter === city.id.toString()}
                                                onClick={() => setCityFilter(city.id.toString())}
                                            >
                                                {city.name}
                                            </Dropdown.Item>
                                        ))}
                                    </Dropdown.Menu>
                                </Dropdown>

                                <Dropdown className="mb-2 mb-md-0">
                                    <Dropdown.Toggle variant="outline-primary" size="sm" id="type-filter" className="px-3">
                                        <i className="fa fa-user-md mr-1"></i> Type: {typeFilter === 'all' ? 'Tous' : types.find(t => t.id.toString() === typeFilter)?.name || 'Tous'}
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu className="dropdown-menu-custom" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                        <Dropdown.Item active={typeFilter === 'all'} onClick={() => setTypeFilter('all')}>
                                            <i className="fa fa-users mr-2"></i> Tous les types
                                        </Dropdown.Item>
                                        <Dropdown.Divider />
                                        {types.map(type => (
                                            <Dropdown.Item
                                                key={type.id}
                                                active={typeFilter === type.id.toString()}
                                                onClick={() => setTypeFilter(type.id.toString())}
                                            >
                                                {type.name}
                                            </Dropdown.Item>
                                        ))}
                                    </Dropdown.Menu>
                                </Dropdown>

                                <Button
                                    variant="primary"
                                    size="sm"
                                    className="ml-md-3"
                                    onClick={handleRefresh}
                                >
                                    <i className="fa fa-sync-alt mr-1"></i> Actualiser
                                </Button>
                            </div>
                        </div>
                    </Card.Body>
                </Card>
            </div>

            {/* Indicateur de chargement global */}
            {loading && (
                <div className="text-center p-4 mb-4">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2">Chargement des données prestataires...</p>
                </div>
            )}

            {/* Message d'erreur */}
            {error && (
                <Alert variant="danger" className="mb-4">
                    <div className="d-flex align-items-center">
                        <i className="fa fa-exclamation-triangle fa-2x mr-3"></i>
                        <div>
                            <h5 className="alert-heading mb-1">Erreur de chargement</h5>
                            <p className="mb-0">{error}</p>
                        </div>
                    </div>
                </Alert>
            )}

            {!loading && !error && (
                <>
                    {/* Statistiques d'état */}

                    <Row className="mb-4">
                        <Col xl={3} md={6} className="mb-3 mb-xl-0">
                            <Card className="h-100 border-0 hover-card" style={{
                                borderRadius: '12px',
                                overflow: 'hidden',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                                transition: 'transform 0.3s ease'
                            }}>
                                <Card.Body style={{
                                    background: 'linear-gradient(135deg, #f0f9ff 0%, #e1f5fe 100%)',
                                    padding: '25px 20px',
                                    position: 'relative'
                                }}>
                                    <div className="stat-icon" style={{
                                        position: 'absolute',
                                        right: '20px',
                                        top: '20px',
                                        fontSize: '2.5rem',
                                        opacity: '0.15',
                                        color: '#4682B4'
                                    }}>
                                        <i className="fa fa-users"></i>
                                    </div>
                                    <div className="stat-content" style={{ position: 'relative', zIndex: '2' }}>
                                        <div className="stat-title" style={{
                                            fontSize: '0.95rem',
                                            marginBottom: '12px',
                                            color: '#6c757d',
                                            fontWeight: '500'
                                        }}>Total des prestataires</div>
                                        <div className="stat-value" style={{
                                            fontSize: '2.2rem',
                                            fontWeight: '700',
                                            marginBottom: '15px',
                                            color: '#4682B4'
                                        }}>{stats?.totalProviders || 0}</div>
                                        <div className="stat-info" style={{
                                            fontSize: '0.85rem',
                                            color: '#6c757d',
                                            display: 'flex',
                                            alignItems: 'center'
                                        }}>
                                            <i className="fa fa-info-circle mr-1"></i> Enregistrés dans le système
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col xl={3} md={6} className="mb-3 mb-xl-0 hover-card">
                            <Card className="h-100 border-0" style={{
                                borderRadius: '12px',
                                overflow: 'hidden',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                                transition: 'transform 0.3s ease'
                            }}
                                >
                                <Card.Body style={{
                                    background: 'linear-gradient(135deg, #f0fff4 0%, #e3fcec 100%)',
                                    padding: '25px 20px',
                                    position: 'relative'
                                }}>
                                    <div className="stat-icon" style={{
                                        position: 'absolute',
                                        right: '20px',
                                        top: '20px',
                                        fontSize: '2.5rem',
                                        opacity: '0.15',
                                        color: '#3CB371'
                                    }}>
                                        <i className="fa fa-wifi"></i>
                                    </div>
                                    <div className="stat-content" style={{ position: 'relative', zIndex: '2' }}>
                                        <div className="stat-title" style={{
                                            fontSize: '0.95rem',
                                            marginBottom: '12px',
                                            color: '#6c757d',
                                            fontWeight: '500'
                                        }}>Prestataires en ligne</div>
                                        <div className="stat-value" style={{
                                            fontSize: '2.2rem',
                                            fontWeight: '700',
                                            marginBottom: '15px',
                                            color: '#3CB371'
                                        }}>{stats?.onlineProviders || 0}</div>
                                        <div className="stat-info" style={{
                                            fontSize: '0.85rem',
                                            display: 'flex',
                                            alignItems: 'center'
                                        }}>
                                            <Badge bg="success" className="px-2" style={{
                                                padding: '5px 10px',
                                                borderRadius: '50px',
                                                fontSize: '0.8rem',
                                                fontWeight: '500'
                                            }}>
                                                {Math.round(((stats?.onlineProviders || 0) / (stats?.totalProviders || 1)) * 100)}%
                                            </Badge>
                                            <span className="ml-2" style={{ color: '#6c757d', marginLeft: '8px' }}>du total</span>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col xl={3} md={6} className="mb-3 mb-xl-0 hover-card">
                            <Card className="h-100 border-0" style={{
                                borderRadius: '12px',
                                overflow: 'hidden',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                                transition: 'transform 0.3s ease'
                            }}>
                                <Card.Body style={{
                                    background: 'linear-gradient(135deg, #fff5f5 0%, #fee2e2 100%)',
                                    padding: '25px 20px',
                                    position: 'relative'
                                }}>
                                    <div className="stat-icon" style={{
                                        position: 'absolute',
                                        right: '20px',
                                        top: '20px',
                                        fontSize: '2.5rem',
                                        opacity: '0.15',
                                        color: '#FF7F50'
                                    }}>
                                        <i className="fa fa-power-off"></i>
                                    </div>
                                    <div className="stat-content" style={{ position: 'relative', zIndex: '2' }}>
                                        <div className="stat-title" style={{
                                            fontSize: '0.95rem',
                                            marginBottom: '12px',
                                            color: '#6c757d',
                                            fontWeight: '500'
                                        }}>Prestataires hors ligne</div>
                                        <div className="stat-value" style={{
                                            fontSize: '2.2rem',
                                            fontWeight: '700',
                                            marginBottom: '15px',
                                            color: '#FF7F50'
                                        }}>{stats?.offlineProviders || 0}</div>
                                        <div className="stat-info" style={{
                                            fontSize: '0.85rem',
                                            display: 'flex',
                                            alignItems: 'center'
                                        }}>
                                            <Badge bg="danger" className="px-2" style={{
                                                padding: '5px 10px',
                                                borderRadius: '50px',
                                                fontSize: '0.8rem',
                                                fontWeight: '500'
                                            }}>
                                                {Math.round(((stats?.offlineProviders || 0) / (stats?.totalProviders || 1)) * 100)}%
                                            </Badge>
                                            <span className="ml-2" style={{ color: '#6c757d', marginLeft: '8px' }}>du total</span>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col xl={3} md={6} className="mb-3 mb-xl-0 hover-card">
                            <Card className="h-100 border-0" style={{
                                borderRadius: '12px',
                                overflow: 'hidden',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                                transition: 'transform 0.3s ease'
                            }}>
                                <Card.Body style={{
                                    background: 'linear-gradient(135deg, #f0f7ff 0%, #e4efff 100%)',
                                    padding: '25px 20px',
                                    position: 'relative'
                                }}>
                                    <div className="stat-icon" style={{
                                        position: 'absolute',
                                        right: '20px',
                                        top: '20px',
                                        fontSize: '2.5rem',
                                        opacity: '0.15',
                                        color: '#5bc0de'
                                    }}>
                                        <i className="fa fa-history"></i>
                                    </div>
                                    <div className="stat-content" style={{ position: 'relative', zIndex: '2' }}>
                                        <div className="stat-title" style={{
                                            fontSize: '0.95rem',
                                            marginBottom: '12px',
                                            color: '#6c757d',
                                            fontWeight: '500'
                                        }}>Activité récente (24h)</div>
                                        <div className="stat-value" style={{
                                            fontSize: '2.2rem',
                                            fontWeight: '700',
                                            marginBottom: '15px',
                                            color: '#5bc0de'
                                        }}>{stats?.recentlyActive || 0}</div>
                                        <div className="stat-info" style={{
                                            fontSize: '0.85rem',
                                            display: 'flex',
                                            alignItems: 'center'
                                        }}>
                                            <Badge bg="info" className="px-2" style={{
                                                padding: '5px 10px',
                                                borderRadius: '50px',
                                                fontSize: '0.8rem',
                                                fontWeight: '500'
                                            }}>
                                                {Math.round(((stats?.recentlyActive || 0) / (stats?.totalProviders || 1)) * 100)}%
                                            </Badge>
                                            <span className="ml-2" style={{ color: '#6c757d', marginLeft: '8px' }}>du total</span>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Carte des prestataires */}
                    <Row className="mb-4">
                        <Col xl={12}>
                            <Card className="border-0 shadow-sm overflow-hidden">
                                <Card.Header className="bg-white d-flex justify-content-between align-items-center py-3">
                                    <div>
                                        <h5 className="card-title mb-0 text-primary">Carte des prestataires</h5>
                                        <small className="text-muted">Distribution géographique des prestataires en temps réel</small>
                                    </div>
                                    <div className="d-flex align-items-center flex-wrap">
                                        <div className="map-controls mr-3">
                                            <ButtonGroup size="sm">
                                                <Button
                                                    variant={mapView === 'standard' ? 'primary' : 'outline-primary'}
                                                    onClick={() => handleMapViewChange('standard')}
                                                >
                                                    <i className="fa fa-map-marker-alt mr-1"></i> Standard
                                                </Button>
                                                <Button
                                                    variant={mapView === 'clusters' ? 'primary' : 'outline-primary'}
                                                    onClick={() => handleMapViewChange('clusters')}
                                                >
                                                    <i className="fa fa-object-group mr-1"></i> Clusters
                                                </Button>
                                            </ButtonGroup>
                                        </div>

                                        <div className="map-stats d-flex">
                                            <Badge
                                                pill
                                                bg="success"
                                                className="d-flex align-items-center mr-2 px-3 py-2"
                                            >
                                                <div className="status-indicator bg-success mr-2" style={{ width: '10px', height: '10px', borderRadius: '50%' }}></div>
                                                <span>En ligne: {Array.isArray(filteredProviders) ? filteredProviders.filter(p => p.isOnline).length : 0}</span>
                                            </Badge>
                                            <Badge
                                                pill
                                                bg="danger"
                                                className="d-flex align-items-center mr-2 px-3 py-2"
                                            >
                                                <div className="status-indicator bg-danger mr-2" style={{ width: '10px', height: '10px', borderRadius: '50%' }}></div>
                                                <span>Hors ligne: {Array.isArray(filteredProviders) ? filteredProviders.filter(p => !p.isOnline).length : 0}</span>
                                            </Badge>
                                            <Badge
                                                pill
                                                bg="primary"
                                                className="d-flex align-items-center px-3 py-2"
                                            >
                                                <i className="fa fa-filter mr-1"></i>
                                                <span>Visibles: {filteredProviders.length}</span>
                                            </Badge>
                                        </div>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-0">
                                    <div style={{ height: '500px' }}>
                                        <ProviderMap
                                            providers={filteredProviders}
                                            onSelectProvider={handleOpenModal}
                                            clusters={locationClusters}
                                            mapType={mapView}
                                        />
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Graphiques et statistiques */}
                    <Row className="mb-4">
                        <Col xl={4} lg={6} md={6} className="mb-3 mb-xl-0">
                            <Card className="h-100 border-0 shadow-sm">
                                <Card.Header className="bg-white py-3">
                                    <h5 className="card-title mb-0 text-primary">Distribution des statuts</h5>
                                </Card.Header>
                                <Card.Body>
                                    <div id="status-chart" className="mt-3">
                                        <ReactApexChart
                                            options={statusChartOptions}
                                            series={[stats?.onlineProviders || 0, stats?.offlineProviders || 0]}
                                            type="donut"
                                            height={300}
                                        />
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col xl={8} lg={6} md={6} className="mb-3 mb-xl-0">
                            <Card className="h-100 border-0 shadow-sm">
                                <Card.Header className="bg-white py-3">
                                    <h5 className="card-title mb-0 text-primary">Statistiques par type de prestataire</h5>
                                </Card.Header>
                                <Card.Body>
                                    {types.length > 0 ? (
                                        <ReactApexChart
                                            options={typeDistributionOptions}
                                            series={prepareTypeData()}
                                            type="bar"
                                            height={300}
                                        />
                                    ) : (
                                        <div className="text-center py-5">
                                            <i className="fa fa-info-circle fa-2x text-muted mb-2"></i>
                                            <p>Aucune donnée de type disponible</p>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Activités récentes et prestataires en ligne */}
                    <Row>
                        <Col xl={6} lg={6} className="mb-3 mb-xl-0">
                            <Card className="h-100 border-0 shadow-sm">
                                <Card.Header className="bg-white py-3">
                                    <h5 className="card-title mb-0 text-primary">Activités récentes</h5>
                                </Card.Header>
                                <Card.Body className="p-0">
                                    <PerfectScrollbar style={{ height: "400px" }}>
                                        <div className="activity-list px-3">
                                            {Array.isArray(providers) && providers.length > 0 ? (
                                                providers
                                                    .sort((a, b) => new Date(b.lastActivityTime) - new Date(a.lastActivityTime))
                                                    .slice(0, 10)
                                                    .map(provider => (
                                                        <div key={provider.id} className="activity-item">
                                                            <div className={`activity-icon ${provider.isOnline ? 'bg-success-light' : 'bg-danger-light'}`}>
                                                                <i className={`fa fa-${provider.isOnline ? 'check' : 'times'} ${provider.isOnline ? 'text-success' : 'text-danger'}`}></i>
                                                            </div>
                                                            <div className="activity-content">
                                                                <div className="activity-title">{provider.providerName}</div>
                                                                <div className="activity-info">
                                                                    {provider.providerTypeName} - {provider.city}
                                                                </div>
                                                                <div className="activity-time d-flex align-items-center justify-content-between mt-2">
                                                                    <span>{getTimeAgo(provider.lastActivityTime)}</span>
                                                                    <div>
                                                                        <OverlayTrigger
                                                                            placement="top"
                                                                            overlay={<BSTooltip>Voir détails</BSTooltip>}
                                                                        >
                                                                            <Button
                                                                                variant="link"
                                                                                size="sm"
                                                                                className="text-primary p-0 mr-2"
                                                                                onClick={() => handleOpenModal(provider)}
                                                                            >
                                                                                <i className="fa fa-eye"></i>
                                                                            </Button>
                                                                        </OverlayTrigger>
                                                                        {provider.email && (
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={<BSTooltip>Contacter</BSTooltip>}
                                                                            >
                                                                                <Button
                                                                                    variant="link"
                                                                                    size="sm"
                                                                                    className="text-primary p-0"
                                                                                    as="a"
                                                                                    href={`mailto:${provider.email}`}
                                                                                >
                                                                                    <i className="fa fa-envelope"></i>
                                                                                </Button>
                                                                            </OverlayTrigger>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))
                                            ) : (
                                                <div className="text-center py-5">
                                                    <i className="fa fa-history fa-2x text-muted mb-2"></i>
                                                    <p>Aucune activité récente à afficher</p>
                                                </div>
                                            )}
                                        </div>
                                    </PerfectScrollbar>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col xl={6} lg={6}>
                            <Card className="h-100 border-0 shadow-sm">
                                <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center">
                                    <h5 className="card-title mb-0 text-primary">Prestataires en ligne</h5>
                                    <Badge bg="success" pill className="px-3 py-2">
                                        {Array.isArray(providers) ? providers.filter(p => p.isOnline).length : 0} actifs
                                    </Badge>
                                </Card.Header>
                                <Card.Body className="p-0">
                                    <PerfectScrollbar style={{ height: "400px" }}>
                                        <div className="activity-list px-3">
                                            {Array.isArray(providers) && providers.filter(p => p.isOnline).length > 0 ? (
                                                providers
                                                    .filter(p => p.isOnline)
                                                    .sort((a, b) => new Date(b.lastActivityTime) - new Date(a.lastActivityTime))
                                                    .map(provider => (
                                                        <div key={provider.id} className="activity-item">
                                                            <div className="activity-icon bg-success-light">
                                                                <i className="fa fa-wifi text-success"></i>
                                                            </div>
                                                            <div className="activity-content">
                                                                <div className="activity-title d-flex justify-content-between">
                                                                    <span>{provider.providerName}</span>
                                                                    <Badge bg="light" text="success" className="px-2 py-1">
                                                                        <i className="fa fa-check-circle mr-1"></i> En ligne
                                                                    </Badge>
                                                                </div>
                                                                <div className="activity-info">
                                                                    {provider.providerTypeName} - {provider.city}
                                                                </div>
                                                                <div className="activity-time d-flex align-items-center justify-content-between mt-2">
                                                                    <span>Dernière activité: {getTimeAgo(provider.lastActivityTime)}</span>
                                                                    <div>
                                                                        <OverlayTrigger
                                                                            placement="top"
                                                                            overlay={<BSTooltip>Voir détails</BSTooltip>}
                                                                        >
                                                                            <Button
                                                                                variant="link"
                                                                                size="sm"
                                                                                className="text-primary p-0 mr-2"
                                                                                onClick={() => handleOpenModal(provider)}
                                                                            >
                                                                                <i className="fa fa-eye"></i>
                                                                            </Button>
                                                                        </OverlayTrigger>
                                                                        {provider.email && (
                                                                            <OverlayTrigger
                                                                                placement="top"
                                                                                overlay={<BSTooltip>Contacter</BSTooltip>}
                                                                            >
                                                                                <Button
                                                                                    variant="link"
                                                                                    size="sm"
                                                                                    className="text-primary p-0"
                                                                                    as="a"
                                                                                    href={`mailto:${provider.email}`}
                                                                                >
                                                                                    <i className="fa fa-envelope"></i>
                                                                                </Button>
                                                                            </OverlayTrigger>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))
                                            ) : (
                                                <div className="text-center py-5">
                                                    <i className="fa fa-wifi fa-2x text-muted mb-2"></i>
                                                    <p>Aucun prestataire en ligne actuellement</p>
                                                </div>
                                            )}
                                        </div>
                                    </PerfectScrollbar>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </>
            )}

            {/* Modal pour les détails du prestataire */}
            <Modal
                show={showModal}
                onHide={handleCloseModal}
                size="lg"
                centered
                backdrop="static"
                className="provider-detail-modal"
            >
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title className="text-primary">
                        <i className="fa fa-user-md mr-2"></i>
                        Détails du prestataire
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedProvider && (
                        <Row>
                            <Col lg={6}>
                                <div className="provider-header mb-4 d-flex align-items-center">
                                    <div className="provider-avatar mr-3">
                                        <div
                                            className={`avatar-circle ${selectedProvider.isOnline ? 'bg-success-light' : 'bg-danger-light'}`}
                                            style={{
                                                width: '60px',
                                                height: '60px',
                                                borderRadius: '50%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '24px',
                                                fontWeight: 'bold',
                                                color: selectedProvider.isOnline ? '#2bc155' : '#f94687'
                                            }}
                                        >
                                            {selectedProvider.providerName.charAt(0).toUpperCase()}
                                        </div>
                                    </div>
                                    <div>
                                        <h4 className="mb-1">{selectedProvider.providerName}</h4>
                                        <Badge
                                            bg={selectedProvider.isOnline ? 'success' : 'danger'}
                                            className="px-3 py-2"
                                        >
                                            <i className={`fa fa-${selectedProvider.isOnline ? 'wifi' : 'power-off'} mr-1`}></i>
                                            {selectedProvider.isOnline ? 'En ligne' : 'Hors ligne'}
                                        </Badge>
                                    </div>
                                </div>

                                <div className="provider-info mb-4">
                                    <h5 className="text-primary mb-3">Informations de contact</h5>
                                    <div className="info-group mb-2">
                                        <div className="info-label text-muted mb-1">Type de prestataire</div>
                                        <div className="info-value font-weight-medium">
                                            <i className="fa fa-user-md mr-2 text-primary"></i>
                                            {selectedProvider.providerTypeName}
                                        </div>
                                    </div>

                                    <div className="info-group mb-2">
                                        <div className="info-label text-muted mb-1">Email</div>
                                        <div className="info-value font-weight-medium">
                                            <i className="fa fa-envelope mr-2 text-primary"></i>
                                            <a href={`mailto:${selectedProvider.email}`}>{selectedProvider.email}</a>
                                        </div>
                                    </div>

                                    <div className="info-group mb-2">
                                        <div className="info-label text-muted mb-1">Téléphone</div>
                                        <div className="info-value font-weight-medium">
                                            <i className="fa fa-phone mr-2 text-primary"></i>
                                            <a href={`tel:${selectedProvider.phoneNumber}`}>{selectedProvider.phoneNumber}</a>
                                        </div>
                                    </div>

                                    {selectedProvider.whatsappPhone && (
                                        <div className="info-group mb-2">
                                            <div className="info-label text-muted mb-1">WhatsApp</div>
                                            <div className="info-value font-weight-medium">
                                                <i className="fa fa-whatsapp mr-2 text-primary"></i>
                                                {selectedProvider.whatsappPhone}
                                            </div>
                                        </div>
                                    )}

                                    <div className="info-group mb-2">
                                        <div className="info-label text-muted mb-1">Localisation</div>
                                        <div className="info-value font-weight-medium">
                                            <i className="fa fa-map-marker-alt mr-2 text-primary"></i>
                                            {selectedProvider.city}
                                        </div>
                                    </div>
                                </div>
                            </Col>

                            <Col lg={6}>
                                <div className={`status-card p-4 rounded mb-4 bg-${selectedProvider.isOnline ? 'success' : 'danger'}-light`}>
                                    <h5 className={`text-${selectedProvider.isOnline ? 'success' : 'danger'} mb-3`}>
                                        <i className={`fa fa-${selectedProvider.isOnline ? 'wifi' : 'power-off'} mr-2`}></i>
                                        État de connexion
                                    </h5>

                                    <div className="info-group mb-2">
                                        <div className="info-label text-muted mb-1">Dernière activité</div>
                                        <div className="info-value font-weight-medium">
                                            <i className="fa fa-history mr-2"></i>
                                            {new Date(selectedProvider.lastActivityTime).toLocaleString()}
                                            <span className="ml-2 text-muted">({getTimeAgo(selectedProvider.lastActivityTime)})</span>
                                        </div>
                                    </div>

                                    <div className="info-group mb-2">
                                        <div className="info-label text-muted mb-1">Adresse IP</div>
                                        <div className="info-value font-weight-medium">
                                            <i className="fa fa-network-wired mr-2"></i>
                                            {selectedProvider.ipAddress}
                                        </div>
                                    </div>

                                    <div className="info-group mb-2">
                                        <div className="info-label text-muted mb-1">Appareil</div>
                                        <div className="info-value font-weight-medium">
                                            <i className="fa fa-laptop mr-2"></i>
                                            {selectedProvider.deviceType || 'Non spécifié'}
                                        </div>
                                    </div>

                                    <div className="info-group mb-2">
                                        <div className="info-label text-muted mb-1">Coordonnées GPS</div>
                                        <div className="info-value font-weight-medium">
                                            <i className="fa fa-map mr-2"></i>
                                            {selectedProvider.lat}, {selectedProvider.lon}
                                        </div>
                                    </div>
                                </div>

                                <div className="action-buttons d-flex">
                                    <Button variant="primary" className="flex-fill mr-2">
                                        <i className="fa fa-comments mr-2"></i> Envoyer un message
                                    </Button>
                                    <Button variant="info" className="flex-fill">
                                        <i className="fa fa-history mr-2"></i> Historique d'activité
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    )}
                </Modal.Body>
                <Modal.Footer className="bg-light">
                    <Button variant="secondary" onClick={handleCloseModal}>
                        <i className="fa fa-times mr-1"></i> Fermer
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

const mapStateToProps = (state) => {
    return {
        providers: state.rapport?.providers || [],
        loading: state.rapport?.loading || false,
        error: state.rapport?.error || null,
        stats: state.rapport?.stats || {
            totalProviders: 0,
            onlineProviders: 0,
            offlineProviders: 0,
            recentlyActive: 0
        }
    };
};

export default connect(mapStateToProps)(ProviderDashboard);